import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { useFixedPanel, useOverlayPopover } from '../useOverlayPopover'

// MPD-64 — the app-wide ⓘ. Pages keep only what is needed to read a figure (share basis,
// freshness, errors, partial periods) as text; definitions, source notes and caveats go in here.
//
// - Mouse: opens on hover; leaving closes it after a short delay, so the pointer can move into
//   the panel (the panel is inside the wrapper, so it keeps it open).
// - Keyboard: opens on focus, closes when focus leaves. Enter/Space pin it open or close it.
// - Touch: a tap toggles it; the hover a touch emulates is ignored.
// - Escape, a press outside and opening another ⓘ close it (useOverlayPopover).
// - The panel overlays the page (useFixedPanel): nothing moves and the viewport edges never
//   clip it.
// - Screen readers: the panel is always mounted and is the button's description, so focusing
//   the ⓘ reads "About X" and then the tip, without opening it. When open it is plain text that
//   follows the button.
// - `active`: as in StatusText. A line that stays mounted but is hidden passes false; the tip
//   closes and holds no listeners.
// - MPD-87: a teal circle with a white "i" (App.css). `muted` is only for an ⓘ placed on a dark
//   ('dark': petrol / steel) or teal ('teal': accent) surface; the sidebar, the user chat bubble
//   and the primary nav item get it from their own class, so a caller there passes nothing.
//   The wrapper carries `info-tip--open` while the panel shows.

type Mode = 'hover' | 'focus' | 'pinned'
const HOVER_CLOSE_MS = 150

export function InfoTip({ label, children, active = true, size, muted }: {
  label: string
  children: ReactNode
  active?: boolean
  size?: 'small'
  muted?: 'dark' | 'teal'
}) {
  const id = useId()
  const wrapRef = useRef<HTMLSpanElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLSpanElement>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [mode, setMode] = useState<Mode | null>(null)
  const open = mode !== null && active

  const close = () => {
    clearTimeout(hoverTimer.current)
    setMode(null)
  }
  const { isRestoringFocus } = useOverlayPopover({ open, close, triggerRef, containerRef: wrapRef })
  useFixedPanel({ open, close, triggerRef, panelRef })
  useEffect(() => { if (!active) close() }, [active])
  useEffect(() => () => clearTimeout(hoverTimer.current), [])

  return (
    <span
      ref={wrapRef}
      className={infoTipClass(size, muted, open)}
      onPointerEnter={(e) => {
        if (e.pointerType !== 'mouse') return
        clearTimeout(hoverTimer.current)
        setMode((m) => m ?? 'hover')
      }}
      onPointerLeave={(e) => {
        if (e.pointerType !== 'mouse') return
        clearTimeout(hoverTimer.current)
        hoverTimer.current = setTimeout(() => setMode((m) => (m === 'hover' ? null : m)), HOVER_CLOSE_MS)
      }}
      onBlur={(e) => {
        if (wrapRef.current?.contains(e.relatedTarget as Node | null)) return
        setMode((m) => (m === 'focus' ? null : m))
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="info-tip-button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        aria-describedby={id}
        onFocus={() => { if (!isRestoringFocus()) setMode((m) => m ?? 'focus') }}
        onClick={(e) => {
          // A tip inside a clickable row or label must not trigger it.
          e.stopPropagation()
          setMode((m) => (m === 'pinned' ? null : 'pinned'))
        }}
      >
        <span className="info-tip-glyph" aria-hidden="true">i</span>
      </button>
      <span ref={panelRef} id={id} className="info-tip-panel" role="note" hidden={!open}>{children}</span>
    </span>
  )
}

function infoTipClass(size: 'small' | undefined, muted: 'dark' | 'teal' | undefined, open: boolean): string {
  return ['info-tip',
    size === 'small' && 'info-tip--small',
    muted && 'info-tip--muted',
    muted === 'teal' && 'info-tip--muted-teal',
    open && 'info-tip--open'].filter(Boolean).join(' ')
}
